import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import type { SuggestionState, SuggestionItem } from '../type/ai-translation.type';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { SuggestionModel } from '../model/suggestion.model';
import { GetSuggestionsDTO } from './ai-translation.repository.interface';
import { AITranslationModel } from '../model/ai-translation.model';
import { OriginalInputService } from './original-input.service';
import { Step } from '../type/ai-translation.enum';

export const SuggestionService = Symbol('SuggestionService');

export interface SuggestionService {
    getModelData: () => SuggestionState;
    reset: () => void;
    readonly onStatesChange: Event<SuggestionState>;

    updateSuggestions: (suggestions: SuggestionItem[]) => void;
    initialize: (callback: () => Promise<string>) => void;
    initState: (params: Partial<SuggestionState>) => void;
    changeActiveSuggestionIdx: (activeSuggestionIdx: number) => void;
}

@injectable()
export class SuggestionServiceImpl implements SuggestionService {
    private _onStatesChangeEmitter = new Emitter<SuggestionState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(SuggestionModel)
        private _model: SuggestionModel,
        @inject(GetSuggestionsDTO)
        private _getSuggestionsDTO: GetSuggestionsDTO,
        @inject(AITranslationModel)
        private _aiTranslationModel: AITranslationModel,
        @inject(OriginalInputService)
        private _originalInputService: OriginalInputService,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    private _getTaskId() {
        return this._aiTranslationModel.modelData.taskId;
    }


    private _setModelData(value: Partial<SuggestionState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public getModelData() {
        return this._model.modelData;
    }

    public initState(params: Partial<SuggestionState>) {
        this._setModelData(params);
    }

    public changeActiveSuggestionIdx = (activeSuggestionIdx: number) => {
        this._setModelData({ activeSuggestionIdx });
    };

    public async initialize(callback: () => Promise<string>) {
        this._setModelData({ isLoading: true });
        const res = await callback();
        this._setModelData({ techTopic: res });
        this.handleTechTopicChange(res);
    }

    public updateSuggestions(suggestions: SuggestionItem[]) {
        this._setModelData({ suggestions });
    }

    public async handleTechTopicChange(techTopic: string) {
        this._setModelData({ techTopic, suggestions: [], isLoading: true });

        const originalInputModel = this._originalInputService.getModelData();
        const taskId = this._getTaskId();
        const suggestions = await this._getSuggestionsDTO.fetch({ ...originalInputModel, techTopic, taskId, currentStep: Step.SUGGESTIONS });

        this._setModelData({ suggestions, isLoading: false });
    }

    public reset() {
        this._setModelData({
            isLoading: true,
            techTopic: '',
            suggestions: [],
            activeSuggestionIdx: 0,
        });
    }
}
